import React from 'react';

interface IconProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
}

const defaultProps = (size = 24) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

// ── Core ──

export const DumbbellIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <path d="M6.5 6.5h11M6.5 17.5h11" />
    <rect x="2" y="8" width="4.5" height="8" rx="1" />
    <rect x="17.5" y="8" width="4.5" height="8" rx="1" />
    <line x1="12" y1="6.5" x2="12" y2="17.5" />
  </svg>
);

export const RunningIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="14" cy="4" r="2" />
    <path d="M7 21l3-7 2.5 2 4.5-6" />
    <path d="M10 14l-3-3 6-4 3 2" />
  </svg>
);

// ── Cardio ──

export const CyclingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="6" cy="17" r="4" />
    <circle cx="18" cy="17" r="4" />
    <path d="M6 17l4-8 4 4h4" />
    <circle cx="10" cy="5" r="2" />
  </svg>
);

export const SwimmingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <path d="M2 18c1.5-1 3-1.5 4.5 0s3 1 4.5 0 3-1.5 4.5 0 3 1 4.5 0" />
    <circle cx="10" cy="6" r="2" />
    <path d="M10 8v3l4 2-6 3" />
  </svg>
);

export const WalkingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M9 21l2-7" />
    <path d="M15 21l-2-7" />
    <path d="M10 14l-1.5-4.5L12 7l3.5 2.5L14 14" />
  </svg>
);

export const HikingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M7 21l3-7" />
    <path d="M14 21l-1-4" />
    <path d="M10 14l-2-5 4-2 3 3-1 4" />
    <line x1="18" y1="3" x2="18" y2="21" />
    <line x1="18" y1="7" x2="21" y2="5" />
  </svg>
);

export const RowingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <path d="M2 20c2-1 4-2 6-2s4 1 6 2 4 1 6-1" />
    <path d="M6 14l4-4 4 2" />
    <ellipse cx="9" cy="16" rx="5" ry="2" />
    <circle cx="14" cy="7" r="2" />
  </svg>
);

export const StairClimberIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <path d="M4 20h4v-4h4v-4h4v-4h4" />
    <circle cx="10" cy="4" r="2" />
    <path d="M8 14l2-4 2 2" />
  </svg>
);

export const EllipticalIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <ellipse cx="12" cy="14" rx="6" ry="4" />
    <circle cx="12" cy="5" r="2" />
    <line x1="12" y1="7" x2="12" y2="10" />
    <line x1="8" y1="12" x2="6" y2="18" />
    <line x1="16" y1="12" x2="18" y2="18" />
  </svg>
);

// ── Training ──

export const HIITIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

export const CrossFitIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M4 10h16" />
    <path d="M12 6v4" />
    <path d="M8 10v6l4 4 4-4v-6" />
  </svg>
);

export const YogaIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M12 6v5" />
    <path d="M8 21l4-10 4 10" />
    <path d="M4 12l8-1 8 1" />
  </svg>
);

export const PilatesIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="7" cy="7" r="2" />
    <path d="M5 12h14" />
    <path d="M7 9v3" />
    <path d="M7 12c0 3 3 5 6 5" />
    <ellipse cx="12" cy="19" rx="8" ry="2" />
  </svg>
);

export const StretchingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="14" cy="4" r="2" />
    <path d="M4 17l6-5 4 1 4-6" />
    <path d="M10 12v9" />
  </svg>
);

export const CalisthenicsIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <line x1="3" y1="8" x2="21" y2="8" />
    <path d="M12 6v2" />
    <path d="M10 10l-2 5 4 2 4-2-2-5" />
    <path d="M8 17l-1 4M16 17l1 4" />
  </svg>
);

// ── Combat ──

export const BoxingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <path d="M4 11c0-2 1.5-4 4-4h1V5a2 2 0 014 0v2h1c2.5 0 4 2 4 4v3c0 3-2.5 5-6 5H10c-3.5 0-6-2-6-5v-3z" />
    <line x1="9" y1="7" x2="9" y2="11" />
    <line x1="15" y1="7" x2="15" y2="11" />
  </svg>
);

export const MartialArtsIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M6 10l6 2 6-4" />
    <path d="M12 12v4" />
    <path d="M8 21l4-5 4 5" />
  </svg>
);

export const WrestlingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="8" cy="4" r="2" />
    <circle cx="16" cy="4" r="2" />
    <path d="M6 9l4 3-2 4-3 4" />
    <path d="M18 9l-4 3 2 4 3 4" />
    <path d="M10 12h4" />
  </svg>
);

// ── Sports ──

export const TennisIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="15" cy="9" r="7" />
    <path d="M8.5 2.5C10 5 10 8 8.5 15.5" />
    <path d="M21.5 8.5C19 10 16 10 8.5 8.5" />
    <line x1="3" y1="21" x2="9" y2="15" />
    <line x1="2" y1="19" x2="5" y2="22" />
  </svg>
);

export const BadmintonIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="15" cy="5" r="3" />
    <line x1="3" y1="21" x2="12.5" y2="7.5" />
    <line x1="2" y1="19" x2="5" y2="22" />
  </svg>
);

export const BasketballIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v18" />
    <path d="M3.5 8.5c3 2 5.5 3 8.5 3s5.5-1 8.5-3" />
    <path d="M3.5 15.5c3-2 5.5-3 8.5-3s5.5 1 8.5 3" />
  </svg>
);

export const SoccerIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="12" r="9" />
    <polygon points="12 7 15 10 14 14 10 14 9 10" />
    <line x1="12" y1="3" x2="12" y2="7" />
    <line x1="21" y1="12" x2="15" y2="10" />
    <line x1="18" y1="19" x2="14" y2="14" />
    <line x1="6" y1="19" x2="10" y2="14" />
    <line x1="3" y1="12" x2="9" y2="10" />
  </svg>
);

export const VolleyballIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3c-2 4-2 8 0 12s2 4 0 6" />
    <path d="M3.5 8c4 1 8-1 12-3" />
    <path d="M3.5 16c4-1 8 1 12 3" />
  </svg>
);

export const GolfIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="20" r="2" />
    <line x1="12" y1="2" x2="12" y2="18" />
    <path d="M12 2l7 4-7 4" />
  </svg>
);

export const TableTennisIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="16" cy="4" r="2.5" />
    <ellipse cx="10" cy="13" rx="6" ry="7" transform="rotate(-30 10 13)" />
    <line x1="14" y1="17" x2="20" y2="22" />
  </svg>
);

// ── Outdoor / Winter ──

export const RockClimbingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M8 21V11l4-5 4 5v10" />
    <line x1="6" y1="8" x2="8" y2="11" />
    <line x1="18" y1="8" x2="16" y2="11" />
    <line x1="10" y1="14" x2="14" y2="14" />
  </svg>
);

export const SkiingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="14" cy="4" r="2" />
    <path d="M6 12l4-2 4 4 4-2" />
    <line x1="2" y1="20" x2="22" y2="14" />
    <line x1="18" y1="2" x2="16" y2="12" />
  </svg>
);

export const SnowboardingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="13" cy="4" r="2" />
    <path d="M8 11l5-1 3 4" />
    <path d="M3 20c3 0 5-1 8-3s5-3 10-2" />
    <path d="M10 10l-3 5" />
  </svg>
);

export const IceSkatingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M10 14l-2 4h-3c-1 0-2 1-2 2" />
    <path d="M14 14l2 4h3c1 0 2 1 2 2" />
    <path d="M10 6l-2 4 4 4 4-4-2-4" />
    <line x1="3" y1="22" x2="8" y2="22" />
    <line x1="16" y1="22" x2="21" y2="22" />
  </svg>
);

export const SurfingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M8 12l4-4 4 4" />
    <path d="M12 8v6" />
    <path d="M2 20c2-2 5-3 8-3s6 1 8 3s4 2 6 0" />
    <ellipse cx="12" cy="18" rx="6" ry="1.5" />
  </svg>
);

export const KayakingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v5" />
    <path d="M7 8l10 4" />
    <ellipse cx="12" cy="16" rx="8" ry="2.5" />
    <path d="M2 20c3-1 6-1.5 10-1.5s7 .5 10 1.5" />
  </svg>
);

// ── Other ──

export const DanceIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M8 21l4-7" />
    <path d="M16 21l-4-7" />
    <path d="M12 6v8" />
    <path d="M6 10l6 2 6-4" />
  </svg>
);

export const JumpRopeIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v5" />
    <path d="M8 21l4-9 4 9" />
    <path d="M5 10c0-5 3-8 7-8s7 3 7 8" />
    <line x1="5" y1="10" x2="5" y2="14" />
    <line x1="19" y1="10" x2="19" y2="14" />
  </svg>
);

export const SkatingIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <circle cx="12" cy="4" r="2" />
    <path d="M10 14l-2 3h-2l-1 2" />
    <path d="M14 14l2 3h2l1 2" />
    <path d="M10 6l-2 4 4 4 4-4-2-4" />
    <circle cx="5" cy="21" r="1.5" />
    <circle cx="19" cy="21" r="1.5" />
  </svg>
);

export const ActivityIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

export const ImportIcon = ({ size, ...props }: IconProps) => (
  <svg {...defaultProps(size)} {...props}>
    <path d="M12 3v12" />
    <path d="M8 11l4 4 4-4" />
    <rect x="4" y="17" width="16" height="4" rx="1" />
    <circle cx="8" cy="19" r="0.5" fill="currentColor" />
    <circle cx="12" cy="19" r="0.5" fill="currentColor" />
    <circle cx="16" cy="19" r="0.5" fill="currentColor" />
  </svg>
);
